import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FileText, ArrowLeft, Download, Package, Wrench, ChevronRight, ExternalLink, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getCaseStudy } from '../services/api';
import { motion } from 'framer-motion';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CaseStudyDetail = () => {
  const { studyId } = useParams();
  const [study, setStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxImg, setLightboxImg] = useState(null);

  useEffect(() => {
    const fetchStudy = async () => {
      try {
        const res = await getCaseStudy(studyId);
        setStudy(res.data);
      } catch {}
      finally { setLoading(false); }
    };
    fetchStudy();
  }, [studyId]);

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_BASE}${url}`;
  };

  const isPdf = (url) => url?.toLowerCase().endsWith('.pdf');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#2E3A6F] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!study) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="border-0 shadow-lg max-w-md w-full mx-4">
          <CardContent className="p-8 text-center space-y-4">
            <FileText className="mx-auto text-gray-300" size={48} />
            <h3 className="text-xl font-bold text-gray-900">Case Study Not Found</h3>
            <Link to="/case-studies">
              <Button className="bg-[#2E3A6F]"><ArrowLeft size={14} className="mr-2" /> Back to Case Studies</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8f9fb] to-white" data-testid="case-study-detail">
      {/* Lightbox */}
      {lightboxImg && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setLightboxImg(null)}>
          <img src={lightboxImg} alt="Trial Report" className="max-w-full max-h-[90vh] object-contain rounded-lg" />
        </div>
      )}

      {/* Header */}
      <section className="bg-[#2E3A6F] text-white py-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-64 h-64 rounded-full bg-white/20 blur-3xl" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl relative z-10">
          <Link to="/case-studies" className="inline-flex items-center text-white/70 hover:text-white text-sm mb-4 transition-colors" data-testid="back-to-case-studies">
            <ArrowLeft size={16} className="mr-1" /> Back to Case Studies
          </Link>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl font-bold mb-2"
          >
            {study.title}
          </motion.h1>
          <div className="flex flex-wrap items-center gap-3 text-white/70 text-sm">
            <span className="bg-white/20 px-3 py-1 rounded-full">{study.project_name}</span>
            {study.category && <span className="bg-white/10 px-3 py-1 rounded-full">{study.category}</span>}
          </div>
        </div>
      </section>

      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Cover Image */}
              {study.cover_image && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="rounded-xl overflow-hidden shadow-lg"
                >
                  <img src={getImageUrl(study.cover_image)} alt={study.title} className="w-full h-auto object-cover" />
                </motion.div>
              )}

              {/* Description */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3">Project Overview</h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">{study.description}</p>
              </div>

              {/* Results */}
              {study.results && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                  <h2 className="text-lg font-bold text-green-900 mb-2">Results & Outcome</h2>
                  <p className="text-green-800 leading-relaxed whitespace-pre-line">{study.results}</p>
                </div>
              )}

              {/* Trial Reports Section */}
              {study.trial_reports?.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Trial Reports</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {study.trial_reports.map((report, i) => {
                      const url = getImageUrl(report);
                      const pdf = isPdf(report);
                      return (
                        <Card key={i} className="border overflow-hidden hover:shadow-md transition-shadow" data-testid={`trial-report-${i}`}>
                          {pdf ? (
                            <CardContent className="p-5 flex items-center gap-4">
                              <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                <FileText size={24} className="text-red-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">Trial Report {i + 1}</p>
                                <p className="text-xs text-gray-500">PDF Document</p>
                              </div>
                              <a href={url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                                <Button size="sm" variant="outline" className="text-[#2E3A6F]">
                                  <Download size={14} className="mr-1" /> View
                                </Button>
                              </a>
                            </CardContent>
                          ) : (
                            <div className="cursor-pointer" onClick={() => setLightboxImg(url)}>
                              <img src={url} alt={`Trial Report ${i + 1}`} className="w-full h-40 object-cover" />
                              <CardContent className="p-3">
                                <p className="text-xs text-gray-600 font-medium">Trial Report {i + 1} - Click to expand</p>
                              </CardContent>
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Highlights */}
              {study.highlights?.length > 0 && (
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Key Highlights</h3>
                    <ul className="space-y-3">
                      {study.highlights.map((h, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                          <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold">&#10003;</span>
                          {h}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Products Used */}
              {study.products?.length > 0 && (
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Package size={18} className="text-[#2E3A6F]" /> Products Used
                    </h3>
                    <div className="space-y-3">
                      {study.products.map((p, i) => (
                        <div key={i} className="bg-gray-50 rounded-lg p-3 space-y-1">
                          <p className="font-semibold text-sm text-gray-900">{p.product_name}</p>
                          {p.component_name && (
                            <p className="text-xs text-gray-600 flex items-center gap-1">
                              <Wrench size={11} /> Component: {p.component_name}
                            </p>
                          )}
                          {p.material && (
                            <p className="text-xs text-gray-600 flex items-center gap-1">
                              <Layers size={11} /> Material: {p.material}
                            </p>
                          )}
                          {p.product_id && (
                            <Link to={`/product/${p.product_id}`} className="text-xs text-[#2E3A6F] hover:underline flex items-center gap-1 mt-1">
                              View Product <ExternalLink size={10} />
                            </Link>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* CTA */}
              <Card className="border-0 bg-[#2E3A6F] text-white shadow-lg">
                <CardContent className="p-6 text-center space-y-3">
                  <h3 className="font-bold">Interested in similar results?</h3>
                  <p className="text-white/80 text-sm">Get in touch with our application engineers for a customized solution.</p>
                  <Link to="/enquiry">
                    <Button className="w-full bg-white text-[#2E3A6F] hover:bg-gray-100 font-semibold">
                      Enquire Now <ChevronRight size={14} className="ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudyDetail;
